
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNotify } from "@/hooks/useNotify";
import { TeamMember, PendingInvitation } from "./useTeamData";

export const useTeamActions = (
  members: TeamMember[],
  setMembers: React.Dispatch<React.SetStateAction<TeamMember[]>>,
  setPendingInvitations: React.Dispatch<React.SetStateAction<PendingInvitation[]>>,
  companyName: string,
  currentCompanyId?: string
) => {
  const notify = useNotify();
  const [suspensionDialog, setSuspensionDialog] = useState<{
    isOpen: boolean;
    memberId: string;
    memberName: string;
  }>({
    isOpen: false,
    memberId: '',
    memberName: ''
  });

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    onConfirm: () => Promise<void>;
  }>({
    isOpen: false,
    title: '',
    description: '',
    onConfirm: async () => {}
  });

  const closeConfirmDialog = () =>
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));

  const handleMakeAdmin = async (memberId: string, memberName: string) => {
    if (!currentCompanyId) {
      notify.error('Company ID is not available');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Promote to Admin',
      description: `Promote ${memberName} to admin? They will be able to manage team members and company settings.`,
      onConfirm: async () => {
        const { data, error } = await supabase
          .rpc('grant_admin_role' as any, {
            p_user_id: memberId,
            p_company_id: currentCompanyId
          } as any);

        console.log('[TeamActions] grant_admin_role result:', { data, error });

        if (error) throw error;

        console.log('[TeamActions] grant_admin_role succeeded for', memberId);

        setMembers(prev => prev.map(member =>
          member.id === memberId
            ? { ...member, role: 'admin', company_position: 'admin' }
            : member
        ));

        notify.success(`${memberName} has been promoted to admin`);
      }
    });
  };

  const handleRemoveAdmin = async (memberId: string, memberName: string) => {
    if (!currentCompanyId) {
      notify.error('Company ID is not available');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Remove Admin Role',
      description: `Remove admin role from ${memberName}? They will no longer be able to manage team members or company settings.`,
      onConfirm: async () => {
        const { data, error } = await supabase
          .rpc('remove_admin_role' as any, {
            p_user_id: memberId,
            p_company_id: currentCompanyId
          } as any);

        if (error) throw error;

        setMembers(prev => prev.map(member =>
          member.id === memberId
            ? { ...member, role: 'user', company_position: 'member' }
            : member
        ));

        notify.success('Admin role has been removed');
      }
    });
  };

  const handleDeactivateMember = async (memberId: string, reason?: string) => {
    try {
      // Get the member's current profile to get company_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', memberId)
        .single();

      if (profileError || !profile?.company_id) {
        throw new Error('Could not find member company information');
      }

      const { data, error } = await supabase.rpc('deactivate_user_from_company', {
        p_user_id: memberId,
        p_company_id: profile.company_id,
        p_reason: reason || 'Removed from company'
      });

      if (error) throw error;

      // Update local state to show member as deactivated
      setMembers(prev => prev.map(member => 
        member.id === memberId 
          ? { 
              ...member, 
              status: 'deactivated' as const,
              deactivated_at: new Date().toISOString(),
              deactivation_reason: reason || 'Removed from company'
            }
          : member
      ));

      notify.success('Member has been deactivated');
    } catch (error: any) {
      console.error('Error deactivating member:', error);
      notify.error(
        error?.message || 'Failed to deactivate member'
      );
    }
  };

  const handleReactivateMember = async (memberId: string) => {
    try {
      // ============ DIAGNOSTIC LOGGING START ============
      console.log('🔍 [REACTIVATE] Starting reactivation process');
      console.log('🔍 [REACTIVATE] Current Settings Company ID:', currentCompanyId);
      console.log('🔍 [REACTIVATE] Target Member ID:', memberId);
      
      // Get the member's current profile to get company_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', memberId)
        .single();

      console.log('🔍 [REACTIVATE] Profile Error:', profileError);
      console.log('🔍 [REACTIVATE] Profile Company ID:', profile?.company_id);

      if (profileError || !profile?.company_id) {
        console.log('🔍 [REACTIVATE] Profile has no company_id, using fallback query');
        
        // If profile doesn't have company_id, get it from deactivated membership
        const { data: membership, error: membershipError } = await supabase
          .from('company_members')
          .select('company_id')
          .eq('user_id', memberId)
          .eq('status', 'deactivated')
          .single();

        console.log('🔍 [REACTIVATE] Membership Error:', membershipError);
        console.log('🔍 [REACTIVATE] Fallback Membership Company ID:', membership?.company_id);
        console.log('🔍 [REACTIVATE] Company ID Match?', membership?.company_id === currentCompanyId ? '✅ YES' : '❌ NO');
        
        if (currentCompanyId && membership?.company_id !== currentCompanyId) {
          console.log('🚨 [REACTIVATE] COMPANY MISMATCH DETECTED!');
          console.log('🚨 [REACTIVATE] Expected:', currentCompanyId);
          console.log('🚨 [REACTIVATE] Found:', membership?.company_id);
        }

        if (membershipError || !membership?.company_id) {
          throw new Error('Could not find member company information');
        }

        console.log('🔍 [REACTIVATE] Calling RPC with Company ID:', membership.company_id);
        const { data, error } = await supabase.rpc('reactivate_company_member', {
          p_user_id: memberId,
          p_company_id: membership.company_id
        });

        if (error) throw error;

        if (!(data as any)?.success) {
          throw new Error((data as any)?.error || 'Failed to reactivate member');
        }
      } else {
        console.log('🔍 [REACTIVATE] Using profile company_id');
        console.log('🔍 [REACTIVATE] Company ID Match?', profile.company_id === currentCompanyId ? '✅ YES' : '❌ NO');
        console.log('🔍 [REACTIVATE] Calling RPC with Company ID:', profile.company_id);
        
        const { data, error } = await supabase.rpc('reactivate_company_member', {
          p_user_id: memberId,
          p_company_id: profile.company_id
        });

        if (error) throw error;

        if (!(data as any)?.success) {
          throw new Error((data as any)?.error || 'Failed to reactivate member');
        }
      }
      // ============ DIAGNOSTIC LOGGING END ============

      // Update local state to show member as active
      setMembers(prev => prev.map(member => 
        member.id === memberId 
          ? { 
              ...member, 
              status: 'active' as const,
              deactivated_at: null,
              deactivated_by: null,
              deactivation_reason: null
            }
          : member
      ));

      notify.success('Member has been reactivated');
    } catch (error) {
      console.error('Error reactivating member:', error);
      notify.error('Failed to reactivate member');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from('company_invitations')
        .delete()
        .eq('id', invitationId);

      if (error) throw error;

      setPendingInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      notify.success('Invitation has been cancelled');
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      notify.error('Failed to cancel invitation');
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      // First get the invitation details
      const { data: invitation, error: inviteError } = await supabase
        .from('company_invitations')
        .select('email, token, company_id, companies(name)')
        .eq('id', invitationId)
        .single();
      
      if (inviteError || !invitation) {
        throw new Error('Could not find invitation details');
      }

      // Get inviter details
      const { data: inviterProfile } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      const inviterName = inviterProfile 
        ? `${inviterProfile.first_name || ''} ${inviterProfile.last_name || ''}`.trim()
        : 'Team Member';

      const { error } = await supabase.functions.invoke('send-invitation', {
        body: { 
          email: invitation.email,
          companyName: invitation.companies?.name,
          inviterName: inviterName,
          invitationId: invitationId,
          token: invitation.token
        }
      });

      if (error) throw error;

      notify.success('Invitation has been resent');
    } catch (error) {
      console.error('Error resending invitation:', error);
      notify.error('Failed to resend invitation');
    }
  };

  const handleSuspendMember = (memberId: string, memberName: string) => {
    setSuspensionDialog({
      isOpen: true,
      memberId,
      memberName
    });
  };

  const handleUnsuspendMember = async (memberId: string, memberName: string) => {
    try {
      const member = members.find(m => m.id === memberId);
      if (member?.status === 'deactivated') {
        notify.error('Cannot unsuspend a deactivated user. Reactivate them first.');
        return;
      }

      // Get company_id from profile to check company suspension state
      const { data: memberProfile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', memberId)
        .single();

      if (memberProfile?.company_id) {
        const { data: companyData } = await supabase
          .from('companies')
          .select('status')
          .eq('id', memberProfile.company_id)
          .single();

        if (companyData?.status === 'suspended') {
          notify.error(
            'Cannot unsuspend individual members while the company is suspended. Unsuspend the company first.'
          );
          return;
        }
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          status: 'active',
          suspended_until: null,
          suspended_by: null,
          suspension_reason: null
        })
        .eq('id', memberId);

      if (error) throw error;

      setMembers(prev => prev.map(member => 
        member.id === memberId 
          ? { 
              ...member, 
              status: 'active' as const,
              suspended_until: null,
              suspended_by: null,
              suspension_reason: null
            }
          : member
      ));

      notify.success(`${memberName} has been unsuspended`);
    } catch (error) {
      console.error('Error unsuspending user:', error);
      notify.error('Failed to unsuspend user');
    }
  };

  const handleSuspensionComplete = () => {
    // Refresh the members list to reflect the suspension
    setMembers(prev => prev.map(member => 
      member.id === suspensionDialog.memberId 
        ? { ...member, status: 'suspended' as const }
        : member
    ));
  };

  return {
    handleMakeAdmin,
    handleRemoveAdmin,
    handleDeactivateMember,
    handleReactivateMember,
    handleCancelInvitation,
    handleResendInvitation,
    handleSuspendMember,
    handleUnsuspendMember,
    suspensionDialog,
    setSuspensionDialog,
    handleSuspensionComplete,
    confirmDialog,
    closeConfirmDialog
  };
};
